import React, { memo, useCallback, useEffect, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { removeEntity } from '@redux/entity/entity-reducer';
import { useTypedSelector } from '@redux/Hooks/storeSelectors';
import { selectMapLayerIdByEntityName, selectSelectedFeatures } from '@redux/selectors/selectors';
import EntityForm from '@features/entities/entity-form/ui/EntityForm';
import { LoadingComponent } from '@components/LoadingComponent/LoadingComponent';
import { useEntityLoader } from '@features/entities/entity-form/model/useEntityLoader';
import { isErrorResponse } from '@utils/utils';
import { useToastContext } from '@context/toastContext';
import './group-selection.scss';
import ErrorComponent from '@components/ErrorComponent/ErrorComponent';
import { ApiError } from '@api/RestClient';
import { usePresetsContext } from '@context/presetsContext';
import { setSourceReloadRequired } from '@redux/map/map-reducer';
import { clearedSelectedMapFeatures } from '@redux/showInfo/showInfoReducer';
import { useFeaturesHighlightingContext } from '@context/featuresHighlightingContext';
import { FormDataObj } from '@utils/types';
import { GroupSelectionOutletContext } from '@features/group-selection/model/types';
import { useGroupSelectionContext } from '@context/groupSelectionContext';

const GroupSelectionForm = memo(() => {
    const [loading, setLoading] = useState<boolean>(false);
    const selectedFeatures = useTypedSelector(selectSelectedFeatures);
    const { groupselectionId } = useParams();
    const { entityName, sourceEntityName, groupUpdateTaskArgument, showDeleteBtn } =
        useOutletContext<GroupSelectionOutletContext>();
    const sourceId = useTypedSelector(state =>
        selectMapLayerIdByEntityName(state, sourceEntityName)
    );
    const {
        loading: isSegmentLoading,
        entity,
        metadata,
        error,
        loadData,
        changeForm,
        groupSaveFn,
        groupDeleteFn,
    } = useEntityLoader<any>({ entityName, groupUpdateTaskArgument });
    const { getGroupSelectionSets } = useGroupSelectionContext();
    const { selectedPresetId } = usePresetsContext();
    const { highlightedFeaturesById } = useFeaturesHighlightingContext();
    const { addToast } = useToastContext();
    const { t } = useTranslation();
    const dispatch = useDispatch();

    useEffect(() => {
        void (async function () {
            await loadData({ entityName });
        })();

        return () => {
            dispatch(removeEntity(entityName));
        };
    }, [groupselectionId]);

    const submitFn = useCallback(
        async (formDataObj: FormDataObj) => {
            setLoading(true);

            const resp = await groupSaveFn({
                selectedFeatures,
                data: formDataObj,
            });

            if (!isErrorResponse(resp)) {
                addToast(`${t('common.save-successful')}`, 'success');
                await loadData({ entityName });
                dispatch(setSourceReloadRequired({ sourceId: sourceId, isReloadRequired: true }));
            }

            setLoading(false);
        },
        [selectedFeatures, entityName]
    );

    const deleteSelectedFn = useCallback(async () => {
        setLoading(true);

        const resp = await groupDeleteFn({
            presetId: selectedPresetId,
            ids: selectedFeatures.map(({ id }) => id),
        });
        if (!isErrorResponse(resp)) {
            await getGroupSelectionSets();
            dispatch(setSourceReloadRequired({ sourceId, isReloadRequired: true }));
            dispatch(clearedSelectedMapFeatures());
            highlightedFeaturesById.current = {};
            addToast(`${t('common.delete-successful')}`, 'success');
        }

        setLoading(false);
    }, [groupDeleteFn, sourceId, selectedPresetId, selectedFeatures, getGroupSelectionSets]);

    return (
        <div className="group-selection-form-container">
            <ErrorComponent error={error as unknown as ApiError}>
                <LoadingComponent isLoading={isSegmentLoading || loading}>
                    <p data-testid="groupSelectionSelectedNum">
                        {t('road-network.group-selection-title', {
                            numItems: selectedFeatures.length,
                        })}
                    </p>
                    {selectedFeatures.length ? (
                        <EntityForm
                            loading={isSegmentLoading || loading}
                            entity={entity}
                            metadata={metadata}
                            changeFormFn={changeForm}
                            saveObjFn={submitFn}
                            showDeleteBtn={showDeleteBtn && !!selectedFeatures.length}
                            deleteTooltip={
                                selectedFeatures.length
                                    ? t('road-network.group-deletion-label')
                                    : ''
                            }
                            deleteFn={deleteSelectedFn}
                            deleteModalTitle={t('road-network.group-deletion-label')}
                        />
                    ) : null}
                </LoadingComponent>
            </ErrorComponent>
        </div>
    );
});

export default GroupSelectionForm;
